import { unwrapRow, type Tables } from '@gymcoach/shared';
import { useQuery } from '@tanstack/react-query';
import { createContext, useContext } from 'react';

import { qk } from '@/lib/queryClient';
import { supabase } from '@/lib/supabase';

import { useAuth } from './AuthProvider';
import type { Role } from './roles';

export type MeProfile = Pick<
  Tables<'profiles'>,
  | 'id'
  | 'gym_id'
  | 'role'
  | 'full_name'
  | 'email'
  | 'phone'
  | 'photo_path'
  | 'status'
  | 'must_change_password'
  | 'sign_in_method'
>;

export type MeGym = Pick<
  Tables<'gyms'>,
  'id' | 'name' | 'slug' | 'logo_path' | 'brand_colour' | 'timezone' | 'currency' | 'grace_days' | 'due_soon_days'
>;

/** The signed-in person as the database knows them. */
export interface Me {
  userId: string;
  profile: MeProfile;
  gym: MeGym;
  role: Role;
  isOwner: boolean;
  /** Owner or admin. */
  isAdmin: boolean;
  isTrainer: boolean;
}

const PROFILE_COLUMNS =
  'id, gym_id, role, full_name, email, phone, photo_path, status, must_change_password, sign_in_method';
const GYM_COLUMNS = 'id, name, slug, logo_path, brand_colour, timezone, currency, grace_days, due_soon_days';

/**
 * Reads the caller's profile and gym. RLS returns no profile for a deactivated account
 * (app.my_gym_id() is null for it), which surfaces as "Not found" (P0002).
 */
async function fetchMe(userId: string): Promise<Me> {
  const profile = unwrapRow(
    await supabase.from('profiles').select(PROFILE_COLUMNS).eq('id', userId).maybeSingle(),
  ) as MeProfile;
  const gym = unwrapRow(await supabase.from('gyms').select(GYM_COLUMNS).eq('id', profile.gym_id).maybeSingle()) as MeGym;
  return {
    userId,
    profile,
    gym,
    role: profile.role,
    isOwner: profile.role === 'owner',
    isAdmin: profile.role === 'owner' || profile.role === 'admin',
    isTrainer: profile.role === 'trainer',
  };
}

/** The query behind `MeProvider`. Screens use `useMe()`. */
export function useMeQuery() {
  const { user } = useAuth();
  const userId = user?.id ?? null;
  return useQuery({
    queryKey: qk.me(userId),
    queryFn: () => fetchMe(userId!),
    enabled: userId !== null,
    staleTime: 5 * 60_000,
  });
}

export const MeContext = createContext<Me | null>(null);

/**
 * The signed-in staff member, their role and their gym. Available on every screen
 * inside the shell (RequireAuth renders it only once loaded), so it is never undefined.
 *
 *   const { role, gym, isAdmin } = useMe();
 */
export function useMe(): Me {
  const me = useContext(MeContext);
  if (!me) throw new Error('useMe must be used inside <RequireAuth> (the signed-in shell).');
  return me;
}
